import React from 'react';
import { cn } from '@/lib/utils';

export interface CinematicCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'elevated' | 'glass' | 'flat';
  hover?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  noBorder?: boolean;
}

export const CinematicCard = React.forwardRef<HTMLDivElement, CinematicCardProps>(
  ({ 
    className, 
    variant = 'default', 
    hover = false,
    padding = 'md',
    noBorder = false,
    children,
    ...props 
  }, ref) => {
    const baseStyles = 'rounded-2xl overflow-hidden transition-all duration-300 ease-out';
    
    const variants = {
      default: cn(
        'bg-card',
        !noBorder && 'border border-border',
        'shadow-lg shadow-black/20'
      ),
      elevated: cn(
        'bg-card',
        !noBorder && 'border border-border',
        'shadow-[0_4px_24px_rgba(0,0,0,0.3)]'
      ),
      glass: cn(
        'backdrop-blur-md bg-background/20',
        !noBorder && 'border border-white/10'
      ),
      flat: cn(
        'bg-card',
        !noBorder && 'border border-border'
      ),
    };

    const paddings = {
      none: '',
      sm: 'p-4',
      md: 'p-6',
      lg: 'p-8',
    };

    const hoverStyles = hover ? {
      default: 'hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30',
      elevated: 'hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.4)]',
      glass: 'hover:bg-background/30 hover:border-white/20',
      flat: 'hover:bg-muted/50',
    }[variant] : '';

    return (
      <div
        ref={ref}
        className={cn(
          baseStyles,
          variants[variant],
          paddings[padding],
          hoverStyles,
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CinematicCard.displayName = 'CinematicCard';

// Card Header subcomponent
export interface CinematicCardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  action?: React.ReactNode;
}

export const CinematicCardHeader = React.forwardRef<HTMLDivElement, CinematicCardHeaderProps>(
  ({ className, title, description, action, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-start justify-between gap-4',
          'border-b border-border/50 pb-4 mb-4',
          className
        )}
        {...props}
      >
        <div className="flex-1 min-w-0">
          {title && (
            <h3 className="font-display font-bold text-xl text-foreground">
              {title}
            </h3>
          )}
          {description && (
            <p className="text-sm text-muted-foreground mt-1">
              {description}
            </p>
          )}
          {children}
        </div>
        {action && (
          <div className="shrink-0">
            {action}
          </div>
        )}
      </div>
    );
  }
);

CinematicCardHeader.displayName = 'CinematicCardHeader';

// Card Content subcomponent
export interface CinematicCardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
}

export const CinematicCardContent = React.forwardRef<HTMLDivElement, CinematicCardContentProps>(
  ({ className, noPadding = false, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          !noPadding && 'p-6',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CinematicCardContent.displayName = 'CinematicCardContent';

// Card Footer subcomponent
export interface CinematicCardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
}

export const CinematicCardFooter = React.forwardRef<HTMLDivElement, CinematicCardFooterProps>(
  ({ className, align = 'right', children, ...props }, ref) => {
    const alignments = {
      left: 'justify-start',
      center: 'justify-center',
      right: 'justify-end',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3 pt-4 mt-4',
          'border-t border-border/50',
          alignments[align],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CinematicCardFooter.displayName = 'CinematicCardFooter';

export default CinematicCard;
